'use es6';

import PropTypes from 'prop-types';
import { Metrics } from '../../Metrics';
import LegacyFormattedCurrency from '../../../components/FormattedCurrency';
import getDateNumberFormattingLocale from '../../getDateNumberFormattingLocale';
import { jsx as _jsx } from "react/jsx-runtime";
const alternativeFractionCurrencies = {
  CLP: 2,
  COP: 2
};
const INTL_DEFAULT_MIN_FRACTIONAL_DIGITS = 2;
const trackFormattedCurrencyUnsupportedPropsFallback = ({
  unit,
  abbreviate,
  useCurrencyCode
}) => {
  let prop;
  if (unit) {
    prop = 'unit';
  } else if (abbreviate) {
    prop = 'abbreviate';
  } else if (useCurrencyCode) {
    prop = 'useCurrencyCode';
  } else {
    prop = 'no-currencyCode';
  }
  Metrics.counter('formatted_currency_unsupported_prop_fallback_count', {
    prop
  }).increment();
};
const trackFormatCurrencyErrorFallback = () => {
  Metrics.counter('format_currency_light_error_fallback_count').increment();
};
const getLocale = options => {
  return options && options.locale || getDateNumberFormattingLocale();
};
const getOptions = options => {
  const opts = {};
  if (options) {
    if (typeof options.precision === 'number') {
      opts.maximumFractionDigits = options.precision;
    }
    if (options.stripInsignificantZeros === false) {
      opts.minimumFractionDigits = options.precision;
    } else if (opts.maximumFractionDigits < INTL_DEFAULT_MIN_FRACTIONAL_DIGITS) {
      // https://github.com/andyearnshaw/Intl.js/issues/123
      opts.minimumFractionDigits = opts.maximumFractionDigits;
    }
    if (typeof options.currencyDisplay === 'string') {
      opts.currencyDisplay = options.currencyDisplay;
    }
    if (typeof options.notation === 'string') {
      opts.notation = options.notation;
    }
  }
  return opts;
};
export function formatNumber(number, options) {
  return new Intl.NumberFormat(getLocale(options), getOptions(options)).format(number);
}
export function formatCurrency(number, options) {
  const opts = Object.assign({
    style: 'currency',
    currency: options.currencyCode,
    formatNegativeValuesWithParenthesis: options.formatNegativeValuesWithParenthesis
  }, getOptions(options));
  const formatNegativeValuesWithParenthesis = options.formatNegativeValuesWithParenthesis && number < 0;
  if (alternativeFractionCurrencies[options.currencyCode]) {
    opts.maximumFractionDigits = alternativeFractionCurrencies[options.currencyCode];
  }
  if (options.simplifyCurrency && number % 1 === 0) {
    opts.maximumFractionDigits = 0;
    // https://github.com/andyearnshaw/Intl.js/issues/123
    opts.minimumFractionDigits = 0;
  }
  const formatter = new Intl.NumberFormat(getLocale(options), opts);
  if (options.parts) {
    if (formatNegativeValuesWithParenthesis) {
      return `(${formatter.formatToParts(number).reduce((acc, {
        type,
        value
      }) => `${acc}${typeof options.parts[type] !== 'undefined' ? options.parts[type] : value}`, '').replace('-', '')})`;
    }
    return formatter.formatToParts(number).reduce((acc, {
      type,
      value
    }) => `${acc}${typeof options.parts[type] !== 'undefined' ? options.parts[type] : value}`, '');
  }
  if (formatNegativeValuesWithParenthesis) {
    return `(${formatter.format(number).replace('-', '')})`;
  }
  return formatter.format(number);
}
export function formatPercentage(number, options) {
  const opts = Object.assign({
    style: 'percent'
  }, getOptions(options));
  return new Intl.NumberFormat(getLocale(options), opts).format(number / 100);
}
export function FormattedCurrency(props) {
  const fallbackToLegacyComponent = props.unit || props.abbreviate || props.useCurrencyCode || !props.currencyCode;
  if (fallbackToLegacyComponent) {
    trackFormattedCurrencyUnsupportedPropsFallback(props);
  }
  if (!fallbackToLegacyComponent) {
    try {
      const formattedCurrency = formatCurrency(props.value, props);
      return /*#__PURE__*/_jsx("span", {
        children: formattedCurrency
      });
    } catch (e) {
      trackFormatCurrencyErrorFallback();
    }
  }
  return /*#__PURE__*/_jsx(LegacyFormattedCurrency, Object.assign({}, props));
}

// Supplying unsupported props will result in this component rendering the
// legacy FormattedCurrency component.
FormattedCurrency.propTypes = {
  abbreviate: PropTypes.bool,
  // not currently supported
  abbreviateThreshold: PropTypes.number,
  // not currently supported
  currencyCode: PropTypes.string,
  formatNegativeValuesWithParenthesis: PropTypes.bool,
  notation: PropTypes.string,
  parts: PropTypes.shape({
    integer: PropTypes.string,
    group: PropTypes.string,
    decimal: PropTypes.string,
    fraction: PropTypes.string,
    literal: PropTypes.string,
    currency: PropTypes.string
  }),
  precision: PropTypes.number,
  simplifyCurrency: PropTypes.bool.isRequired,
  stripInsignificantZeros: PropTypes.bool.isRequired,
  unit: PropTypes.string,
  // not currently supported
  useCurrencyCode: PropTypes.bool,
  // not currently supported
  value: PropTypes.number.isRequired
};
FormattedCurrency.defaultProps = {
  formatNegativeValuesWithParenthesis: false,
  stripInsignificantZeros: false,
  simplifyCurrency: false
};
export function FormattedPercentage(props) {
  return formatPercentage(props.value, props);
}